import { useState } from "react"
import { toast } from "@/v2-ui/toast"
import ActionMenu from "@/v2-backoffice-client/components/actionMenu"
import LicensePlanMigrationDialog from "@/v2-backoffice-client/license/plan/view/LicensePlanMigrationDialog"
import { usePlanMigrationDeleteMutation } from "@/v2-backoffice-client/license/plan/view/license.plan.migration.delete.mutation"
import { evictPlanMigrationQueries } from "@/v2-backoffice-client/license/license.utils"
import type { PlanMigrationsQuery } from "@/v2-backoffice-client/license/plan/view/__types__/license.plan.migrations.query"

export type LicensePlanMigrationRowToolbarProps = {
  migration: PlanMigrationsQuery["getPlanMigrations"][number]
  fromPlanId: number
  suggestedToPlanId?: number | null
}

// A worklist row's actions: update the conversation (the row editor), open
// the customer it is about, and — only for rows started by mistake — remove
// the record. There is deliberately no "mark completed": completion is the
// customer leaving the license, which Fenerum's mirror records on its own.
function LicensePlanMigrationRowToolbar(
  props: LicensePlanMigrationRowToolbarProps,
) {
  const { migration, fromPlanId, suggestedToPlanId } = props
  const [isEditOpen, setIsEditOpen] = useState(false)

  const [deleteMigration, { loading, error }] = usePlanMigrationDeleteMutation({
    update: evictPlanMigrationQueries,
    onCompleted() {
      toast({
        variant: "success",
        title: "Migration record removed",
        description: `${migration.CustomerName ?? "The customer"} is back to "Not started".`,
      })
    },
  })

  return (
    <>
      <ActionMenu>
        <ActionMenu.Trigger />
        <ActionMenu.Content>
          <ActionMenu.Item
            iconSrc="edit"
            data-feature-code="UI_BackofficeLicensePlan_Migration_Row_Update"
            onSelect={(e) => {
              e.preventDefault()
              setIsEditOpen(true)
            }}
          >
            Update Migration
          </ActionMenu.Item>

          {migration.ExternalCustomerId && (
            <ActionMenu.LinkItem
              iconSrc="expand"
              to={`>>?v=customer:${migration.ExternalCustomerId}`}
            >
              Open Customer
            </ActionMenu.LinkItem>
          )}

          {migration.MigrationId != null && (
            <ActionMenu.ConfirmItem
              iconSrc="trash"
              variant="destructive"
              isLoading={loading}
              error={error}
              errorTitle="Failed to remove migration record"
              confirmTitle="Remove Migration Record"
              confirmDescription={`Removes the migration record for ${migration.CustomerName ?? "this customer"} — status, target and note. Use it for rows started by mistake; the customer stays on the license and returns to "Not started".`}
              confirmText="Remove"
              confirmVariant="destructive"
              data-feature-code="UI_BackofficeLicensePlan_Migration_Row_Delete"
              onConfirm={async () => {
                if (migration.MigrationId == null) return
                await deleteMigration({
                  variables: { id: migration.MigrationId },
                })
              }}
            >
              Remove Record
            </ActionMenu.ConfirmItem>
          )}
        </ActionMenu.Content>
      </ActionMenu>

      {/* Mounted per open so the fields re-seed from the row every time —
          save, reopen, and the status/target/note you just set are there. */}
      {isEditOpen && (
        <LicensePlanMigrationDialog
          migration={migration}
          fromPlanId={fromPlanId}
          suggestedToPlanId={suggestedToPlanId}
          open={isEditOpen}
          onOpenChange={setIsEditOpen}
        />
      )}
    </>
  )
}

export default LicensePlanMigrationRowToolbar
